<script setup lang="ts">
import { ref } from 'vue'
import { VueRecaptcha } from 'vue-recaptcha'

/**
 * Google's invisible reCAPTCHA, as one control.
 *
 * Invisible means it renders nothing until it has a reason to: the caller validates its
 * form, calls `execute()`, and waits for `verify` to hand back the token that goes in the
 * payload. A challenge only appears when Google wants one.
 *
 * Never import `vue-recaptcha` in a slice: the package is barely maintained, and keeping it
 * named in one file means swapping it is a change here and not in every form that asks.
 *
 * **Nothing is mounted without a `sitekey`.** An empty one is a local checkout with no
 * `VITE_CAPTCHA_KEY`: `vue-recaptcha` would still fetch Google's script and log a failure
 * for a key that is not there. The caller's side of this is `useCaptchaGuard`, which sends
 * the form without a token instead of waiting for a `verify` that can never arrive.
 */
defineProps<{
  sitekey: string
}>()

const emit = defineEmits<{
  (e: 'verify', token: string): void
  /** The token went stale before the form was sent; ask again. */
  (e: 'expired'): void
  /**
   * The widget exists and `execute()` will now do something. Before this, it does
   * nothing at all: `vue-recaptcha` returns early while it has no widget id, and Google's
   * script is fetched asynchronously — so a caller that executes on mount silently never
   * gets a token. A form triggered by a button never notices; one triggered by page load
   * has to wait for this.
   */
  (e: 'ready'): void
}>()

const widget = ref<{ execute: () => void; reset: () => void }>()

defineExpose({
  execute: () => widget.value?.execute(),
  reset: () => widget.value?.reset()
})
</script>

<template>
  <VueRecaptcha
    v-if="sitekey"
    ref="widget"
    :sitekey="sitekey"
    :load-recaptcha-script="true"
    size="invisible"
    @render="() => emit('ready')"
    @verify="(token: string) => emit('verify', token)"
    @expired="() => emit('expired')"
  />
</template>
